import React, { useState, useEffect, useRef } from 'react';
import {
  ChakraProvider,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Wrap,
  WrapItem,
  useColorModeValue,
  Link,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  useToast,
  Icon,
} from '@chakra-ui/react';
import Confetti from 'react-confetti'; // Import Confetti
import posthog from 'posthog-js'; // Import PostHog

// Initialize PostHog
posthog.init('phc_m2ZQetQhhqdHMQjRgkB5hBOu4q2sDE63pyO1FUzf9uC', {
    api_host: 'https://eu.i.posthog.com',
    person_profiles: 'identified_only' // or 'always' to create profiles for anonymous users as well
});

const festiveWords = [
  'new_years', 'greeting', 'healthy', 'sleep', 'happiness', 'wealth', 'nice_to_have', 'boring',
  'hamburger', 'dream_come_true', 'feeling', 'wedding', 'kids', 'best_year_ever', 'family',
  'friends', 'fun', 'sun', 'wolt', 'cat', 'dog', 'work', 'collaborate', 'wealth', 'office',
  'learn', 'random', 'song', 'AI', 'wine', 'beer', 'cocktail', 'cheers', 'beach', 'flight',
  'gym', 'vacation', 'surf', 'promotion', 'hobbies', 'free_time', 'date', 'ocean'
];

const toneKeywords = [
  'Fun', 'Serious', 'Humorous', 'Inspirational', 'Romantic',
  'Mysterious', 'Poetic', 'Adventurous'
];

const backgrounds = [
  { name: 'Dark Code', value: 'linear-gradient(to right, #1a1a1a, #2a2a2a)' },
  { name: 'Matrix Green', value: 'linear-gradient(to right, #000000, #003300)' },
  { name: 'Blue Tech', value: 'linear-gradient(to right, #000428, #004e92)' },
  { name: 'Sunset', value: 'linear-gradient(to right, #ff512f, #dd2476)' },
  { name: 'Northern Lights', value: 'linear-gradient(to right, #43cea2, #185a9d)' },
  { name: 'Cosmic', value: 'linear-gradient(to right, #360033, #0b8793)' },
  { name: 'Golden Hour', value: 'linear-gradient(to right, #ffd89b, #19547b)' },
  { name: 'Deep Space', value: 'linear-gradient(to right, #000000, #434343)' },
  { name: 'Firestorm', value: 'linear-gradient(to right, #ff4e50, #f9d423)' },
{ name: 'Ocean Breeze', value: 'linear-gradient(to right, #00c6ff, #0072ff)' },
{ name: 'Purple Rain', value: 'linear-gradient(to right, #a18cd1, #fbc2eb)' },
{ name: 'Midnight Horizon', value: 'linear-gradient(to right, #020024, #090979, #00d4ff)' },


];

const DownArrowIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M12 16.5l-6-6h12z" />
  </Icon>
);

const SQLGreetingCardGenerator = () => {
  const [selectedWords, setSelectedWords] = useState([]);
  const [selectedTone, setSelectedTone] = useState('');
  const [greeting, setGreeting] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [selectedBackground, setSelectedBackground] = useState(backgrounds[0].value);
  const [isLoading, setIsLoading] = useState(false);
  const [webhookData, setWebhookData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // New state for modal visibility
  const [showConfetti, setShowConfetti] = useState(false); // New state for confetti visibility
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false); // New state for instructions modal

  const toast = useToast(); // Initialize toast

  const addWord = (word) => {
    if (selectedWords.length < 7 && !selectedWords.includes(word)) {
      setSelectedWords((prevWords) => [...prevWords, word]);
    }
  };

  const removeWord = (word) => {
    setSelectedWords((prevWords) => prevWords.filter((w) => w !== word));
  };

  const selectTone = (tone) => {
    setSelectedTone(tone);
  };

  const getRandomElement = (array) => array[Math.floor(Math.random() * array.length)];

  const getCreativeSQLFunction = () => {
    const functions = [
      "CONCAT",
      "UPPER",
      "LOWER",
      "REVERSE",
      "SUBSTRING",
      "REPLACE",
      "LENGTH",
    ];
    return getRandomElement(functions);
  };

  const getCreativeJoin = () => {
    const joins = [
      "INNER JOIN feelings ON greeting.mood = feelings.mood",
      "LEFT JOIN memories ON greeting.year = memories.year",
      "RIGHT JOIN dreams ON greeting.aspiration = dreams.goal",
      "FULL OUTER JOIN adventures ON greeting.excitement = adventures.thrill_level",
    ];
    return getRandomElement(joins);
  };

  const getCreativeWhere = () => {
    const conditions = [
      "WHERE happiness_level > 9000",
      "WHERE dreams_achieved >= expectations",
      "WHERE love_quotient = 'overflowing'",
      "WHERE adventure_spirit = 'unstoppable'",
    ];
    return getRandomElement(conditions);
  };

  const generateSQLGreeting = async () => {
    if (selectedWords.length === 0 || !selectedTone) {
        alert('Please select at least one word and a tone before generating the greeting.');
        return;
    }

    setIsLoading(true);

    try {
        const response = await fetch(`https://hook.eu1.make.com/ti5tigj7y7v0vmahpry1cdkgem7q94bm`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                selectedWords,
                selectedTone,
                background: selectedBackground,
            }),
        });

        if (!response.ok) {
            throw new Error(`Failed to send data to webhook: ${response.status} ${response.statusText}`);
        }

        const webhookResponseText = await response.text(); // Get the text body of the response
        setGreeting(formatGreeting(webhookResponseText)); // Format the response text with SQL syntax highlighting

    } catch (error) {
        console.error('Error:', error.message);
        setGreeting(`Failed to process request: ${error.message}`);
    } finally {
        setIsLoading(false);
    }
};

  const fetchWebhookSiteData = async () => {
    const webhookSiteUrl = 'http://localhost:8010/proxy/3458dcb5-29c5-4813-8e77-c9d6a55a1149';
    try {
      const response = await fetch(webhookSiteUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get("content-type");
      let data;
      let isJson = false;

      if (contentType && contentType.indexOf("application/json") !== -1) {
        data = await response.json();
        isJson = true;
      } else {
        data = await response.text();
      }

      setWebhookData(data);
      setGreeting(generateGreetingFromWebhookData(data, isJson));
    } catch (error) {
      console.error('Error fetching from webhook.site:', error);
      setGreeting(`Failed to process data from webhook.site: ${error.message}`);
    }
  };

  const generateGreetingFromWebhookData = (data, isJson) => {
    if (isJson) {
      // Handle JSON data
      if (!data || !Array.isArray(data.words) || !data.tone) {
        console.error('Unexpected JSON structure:', data);
        return `-- Error: Unexpected JSON structure
SELECT 'Please check console for details' AS error_message;`;
      }

      return `
-- ${data.tone} SQL Greeting
SELECT 
  ${data.words.map(word => `'${word}' AS ${word.replace(/[^a-zA-Z0-9]/g, '_')}`).join(',\n  ')}
FROM dual
WHERE happiness_level > ${Math.floor(Math.random() * 100)};
      `.trim();
    } else {
      // Handle text data
      return `
-- Text Response from Webhook
SELECT '${data.replace(/'/g, "''")}' AS webhook_response
FROM dual;
      `.trim();
    }
  };

  const resetGreeting = () => {
    setSelectedWords([]);
    setSelectedTone('');
    setGreeting('');
    setShareUrl('');
  };

  const generateShareUrl = () => {
    // Implementation remains the same
    // {{ edit_1 }} // Add your share URL generation logic here
    setShowConfetti(true); // Show confetti when sharing
    setTimeout(() => setIsModalOpen(true), 100); // Open the modal after a short delay
  };

  const formatGreeting = (text) => {
    // Return formatted SQL with styles and indentation
    return text
        .replace(/SELECT/g, '<strong class="sql-keyword">SELECT</strong>')
        .replace(/FROM/g, '<strong class="sql-keyword">FROM</strong>')
        .replace(/WHERE/g, '<strong class="sql-keyword">WHERE</strong>')
        .replace(/'([^']+)'/g, '<span style="color: lightgreen; font-weight: bold;">\'$1\'</span>') // Style strings with quotes
        .replace(/,/g, ', ') // Add space after commas for better readability
        .replace(/\n/g, '<br/>') // Replace new lines with <br/>
        .replace(/(\s+)/g, '&nbsp;'); // Replace spaces with non-breaking spaces for indentation
  };

  // New function to parse SQL and format it
  const parseSQL = (sql) => {
    // Basic parsing logic (you can enhance this as needed)
    return sql
        .replace(/SELECT/g, '<strong>SELECT</strong>')
        .replace(/FROM/g, '<strong>FROM</strong>')
        .replace(/WHERE/g, '<strong>WHERE</strong>')
        .replace(/,/g, ',<br/>'); // Add line breaks after commas for better readability
  };

  // New function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setShowConfetti(false); // Hide confetti when modal closes
  };

  // New function to handle sharing
  const handleShare = () => {
    const email1 = document.querySelector('input[placeholder="My email"]').value;
    const email2 = document.querySelector('input[placeholder="My friend\'s email"]').value;
    
    if (!email1 || !email2) {
        alert('Please enter both email addresses.');
        return;
    }

    // Save recent keywords and mood
    const recentKeywords = selectedWords.join(', '); // Join selected keywords as a string
    const recentMood = selectedTone; // Capture the selected tone

    // Save to local storage
    localStorage.setItem('recentKeywords', recentKeywords); // Save recent keywords
    localStorage.setItem('recentMood', recentMood); // Save recent mood

    // Log to verify values before sending
    console.log('Recent Keywords:', recentKeywords);
    console.log('Recent Mood:', recentMood);

    // New PostHog event tracking
    posthog.capture('greeting_shared', {
        sender_email: email1,
        recipient_email: email2,
        greeting: formatGreeting(greeting), // Use the updated formatGreeting function
        background: selectedBackground,
        keywords: recentKeywords, // Send recent keywords
        mood: recentMood, // Send recent mood
        gradient_bg_color: selectedBackground, // Add gradient background color
        project_id: 33541,
        project_region: 'EU Cloud',
    });

    // Log the event properties to verify they are sent
    console.log('Event Properties:', {
        sender_email: email1,
        recipient_email: email2,
        greeting: formatGreeting(greeting),
        background: selectedBackground,
        keywords: recentKeywords,
        mood: recentMood,
        gradient_bg_color: selectedBackground,
    });

    // New API call to PostHog
    fetch('https://eu.i.posthog.com/capture/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            api_key: 'phc_xiTDgCjcE6DHuX20IqPSLeFrk3skRlKZJaqROFIXv6E',
            event: 'greeting_shared',
            properties: {
                distinct_id: email1, // Assuming email1 is the distinct ID
                sender_email: email1, // Send sender email
                recipient_email: email2, // Send recipient email
                greeting: formatGreeting(greeting), // Use the updated formatGreeting function
                background: selectedBackground,
            },
        }),
    }).then(response => {
        if (!response.ok) {
            console.error('Failed to send event to PostHog:', response.statusText);
        }
    }).catch(error => {
        console.error('Error sending event to PostHog:', error);
    });

    // Additional event capture example
    posthog.capture('my_event', {
        sender: email1,
        recipient: email2,
        greeting_length: greeting.length,
    });

    toast({ // Show toast notification
      title: "We've sent your SQL greeting!",
      status: "success",
      duration: 3000,
      isClosable: true,
    });

    closeModal(); // Close the modal after sharing
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <ChakraProvider>
      {showConfetti && <Confetti />} {/* Render Confetti component */}
      <Box h="100vh" p={2}>
        <VStack h="full" spacing={2} align="stretch">
          <HStack w="full" justifyContent={{ base: "center", md: "space-between" }} alignItems="center" flexWrap="wrap">
            <Button
              size={{ base: "md", md: "lg" }}
              colorScheme="blue"
              variant="solid"
              leftIcon={<Text fontSize="xl">🚀</Text>}
              rightIcon={<Text fontSize="xl">✨</Text>}
              fontWeight="bold"
              px={6}
              py={3}
              borderRadius="full"
              bgGradient="linear(to-r, blue.400, purple.500)"
              _hover={{
                bgGradient: "linear(to-r, blue.500, purple.600)",
              }}
              onClick={() => setIsInstructionsModalOpen(true)} // Open instructions modal
            >
              SQL Greetings: Code Your Wishes
            </Button>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" textAlign={{ base: "center", md: "left" }}>
              Made with ❤️ by <Link href="https://www.sherloqdata.io/" isExternal color="blue.500">Sherloq</Link>
            </Text>
          </HStack>
          <HStack w="full" h="calc(100vh - 80px)" alignItems="flex-start" spacing={2} flexDirection={{ base: "column", md: "row" }}>
            <VStack flex={2} h="full" spacing={2} align="stretch">
              <Heading size="sm" mb={2}>Choose 5-7 keywords:</Heading>
              <Box flex={1} borderWidth={1} borderRadius="lg" p={2} overflowY="auto" h={{ base: "75px", md: "auto" }}>
                <Wrap spacing={2}>
                  {festiveWords.map((word) => (
                    <WrapItem key={word}>
                      <Button
                        size="sm"
                        onClick={() => addWord(word)}
                        colorScheme={selectedWords.includes(word) ? 'blue' : 'gray'}
                        variant={selectedWords.includes(word) ? 'solid' : 'outline'}
                        isDisabled={selectedWords.length >= 7 && !selectedWords.includes(word)}
                      >
                        {word}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
                <Box mt={2}>
                  <Heading size="xs" mb={1}>Selected Words:</Heading>
                  <Wrap spacing={2}>
                    {selectedWords.map((word) => (
                      <WrapItem key={word}>
                        <Button
                          size="sm"
                          onClick={() => removeWord(word)}
                          colorScheme="blue"
                          rightIcon={<Text>×</Text>}
                        >
                          {word}
                        </Button>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </Box>
              <Heading size="sm" mb={2}>Choose a mood:</Heading>
              <Box flex={0.5} borderWidth={1} borderRadius="lg" p={2}>
                <Wrap spacing={2}>
                  {toneKeywords.map((word) => (
                    <WrapItem key={word}>
                      <Button
                        size="sm"
                        onClick={() => selectTone(word)}
                        colorScheme={selectedTone === word ? 'green' : 'gray'}
                        variant={selectedTone === word ? 'solid' : 'outline'}
                      >
                        {word}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
              <Heading size="sm" mb={2}>Choose a background:</Heading>
              <Box flex={0.5} borderWidth={1} borderRadius="lg" p={2}>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={1}>
                  {backgrounds.map((bg) => (
                    <Button
                      key={bg.name}
                      onClick={() => setSelectedBackground(bg.value)}
                      h="30px"
                      w="100%"
                      bgGradient={bg.value}
                      _hover={{ opacity: 0.8 }}
                      position="relative"
                    >
                      <Text
                        position="absolute"
                        bottom="2px"
                        left="2px"
                        fontSize="2xs"
                        bg="blackAlpha.600"
                        color="white"
                        px={1}
                        py={0.5}
                        borderRadius="sm"
                      >
                        {bg.name}
                      </Text>
                    </Button>
                  ))}
                </SimpleGrid>
              </Box>
            </VStack>
            <Box flex={1} h="full" borderWidth={1} borderRadius="lg" p={2} w={{ base: "100%", md: "100%" }}> {/* Set width to 200% for mobile */}
              <Heading size="sm" mb={2}>Your Greeting</Heading>
              <Box
                p={2}
                borderRadius="md"
                mb={2}
                fontFamily="mono"
                fontSize={{ base: "lg", md: "xs" }} // Font size for mobile
                color="white"
                overflow="auto"
                bgGradient={selectedBackground}
                h={{ base: "300px", md: "calc(100% - 140px)" }} // Increased height for mobile
                w={{ base: "100%", md: "auto" }} // Set width to 100% for mobile
                dangerouslySetInnerHTML={{ __html: formatGreeting(greeting) }}
              />
              <VStack spacing={1}>
                <Button
                  onClick={generateSQLGreeting}
                  colorScheme="pink"
                  w={{ base: "100%", md: "100%" }} // Full width on mobile, auto on desktop
                  size="sm"
                  isLoading={isLoading}
                  loadingText="Generating..."
                >
                  Generate SQL Greeting
                </Button>
                <Button
                  onClick={generateShareUrl}
                  colorScheme="pink"
                  w={{ base: "100%", md: "100%" }} // Full width on mobile, auto on desktop
                  size="sm"
                  isDisabled={!greeting}
                >
                  Share Greeting
                </Button>
                <Button
                  onClick={resetGreeting}
                  variant="outline"
                  w={{ base: "100%", md: "100%" }} // Full width on mobile, auto on desktop
                  size="sm"
                >
                  Reset Greeting
                </Button>
              </VStack>
            </Box>
          </HStack>
        </VStack>
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Your Greeting Card 🤩 </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box
                p={2}
                borderRadius="md"
                fontFamily="mono"
                fontSize="xs"
                color="white"
                bgGradient={selectedBackground}
                dangerouslySetInnerHTML={{ __html: formatGreeting(greeting) }}
              />
              <VStack spacing={1} mt={4}>
                <Input placeholder="My email" />
                <Input placeholder="My friend's email" />
                <Button colorScheme="blue" onClick={handleShare}>Send</Button>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Modal isOpen={isInstructionsModalOpen} onClose={() => setIsInstructionsModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Instructions</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>1. Select 5-7 keywords.</Text>
              <Text mt={2}>2. Choose your greeting mood.</Text>
              <Text mt={2}>3. Pick a background for your card.</Text>
              <Text mt={2} fontWeight="bold">Generate your greeting with AI and send it to your geekiest, data-loving friend! 😎</Text>
              <Button mt={4} colorScheme="blue" onClick={() => setIsInstructionsModalOpen(false)}>
                Let's create my own SQL greeting!
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Button
          position="fixed"
          bottom="20px"
          right="20px"
          onClick={scrollToBottom}
          display={{ base: 'flex', md: 'none' }} // Show only on mobile
          colorScheme="teal"
          borderRadius="full" // Make the button circular
          size="md" // Use medium size for the button
          width="50px" // Set a fixed width for the circular button
          height="50px" // Set a fixed height for the circular button
          display="flex" // Use flex to center the icon
          alignItems="center" // Center vertically
          justifyContent="center" // Center horizontally
        >
          <DownArrowIcon boxSize={4} /> {/* Adjust the size of the arrow icon */}
        </Button>
      </Box>
    </ChakraProvider>
  );
};

export default SQLGreetingCardGenerator;